import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { showNotification } from "../../utils";
import "react-notifications-component/dist/theme.css";
import "./notifications.css";
import Select from "../Select";
import { ROL_OPTIONS } from "../../consts";
import Api from "../../api";
import { Editor } from "primereact/editor";
import DOMPurify from "dompurify";
import Modal from "../Modal";

const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = DOMPurify.sanitize(html);
  return (tmp.textContent || tmp.innerText || "").trim();
};

const truncateText = (text, max = 120) => {
  if (!text || text.length <= max) return text;
  return `${text.slice(0, max).trim()}…`;
};

const Notifications = () => {
  // Límite local configurable (caracteres incluyendo HTML). Cambiar aquí al aumentar en backend.
  const MESSAGE_MAX_LENGTH = 750;
  // Toggle temporal para la prueba de imágenes. Ajustar a false o comentar después de validar.
  const IMAGE_FEATURE_TEMP = false; // <- poner en false tras pruebas si se desea desactivar
  const [notification, setNotification] = useState({
    message: "",
    roles: [],
    start_date: "",
    end_date: "",
    customers: "", // Nuevo campo para los usuarios específicos
  });

  const [sendToSpecificUsers, setSendToSpecificUsers] = useState(false);
  const [richMessage, setRichMessage] = useState("");
  const [messageLength, setMessageLength] = useState(0);
  const editorRef = useRef(null);
  const [latestNotifications, setLatestNotifications] = useState([]);
  const [loadingLatest, setLoadingLatest] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const fetchLatestNotifications = useCallback(async () => {
    setLoadingLatest(true);
    try {
      const data = await Api.Notification.GetLatest(10);
      setLatestNotifications(Array.isArray(data) ? data : []);
    } catch (error) {
      showNotification("Error", error.message, "danger");
    } finally {
      setLoadingLatest(false);
    }
  }, []);

  useEffect(() => {
    fetchLatestNotifications();
  }, [fetchLatestNotifications]);

  // Debug: log raw stored rich text (post link-normalization, pre preview enhancements)
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      // Shows exactly what would be sent if we decide to persist this field
      // (Currently only link protocol normalized; no target/rel injected here)
      console.log("[RichMessage:raw-html]", richMessage);
    }
  }, [richMessage]);

  // Inserción controlada de imagen por URL evitando diálogo local
  const handleInsertImage = useCallback(() => {
    if (!IMAGE_FEATURE_TEMP) return;
    if (!editorRef.current) return;
    const quill = editorRef.current.getQuill();
    if (!quill) return;
    const url = window.prompt("Ingrese la URL de la imagen (https://...)");
    if (!url) return;
    const cleanUrl = url.trim();
    if (!/^https?:\/\//i.test(cleanUrl)) {
      showNotification(
        "Error",
        "La URL debe iniciar con http:// o https://",
        "danger"
      );
      return;
    }
    const allowedExt = /(\.png|\.jpe?g|\.gif|\.webp|\.svg)(\?.*)?$/i;
    if (!allowedExt.test(cleanUrl)) {
      showNotification(
        "Aviso",
        "La URL no parece una imagen estándar, se insertará de todas formas.",
        "warning"
      );
    }
    const range = quill.getSelection(true) || {
      index: quill.getLength(),
      length: 0,
    };
    quill.insertEmbed(range.index, "image", cleanUrl, "user");
    quill.setSelection(range.index + 1, 0);
    setTimeout(() => {
      const html = editorRef.current?.getQuill()?.root?.innerHTML || "";
      setMessageLength(html.length);
      if (html.length > MESSAGE_MAX_LENGTH) {
        showNotification(
          "Aviso",
          `El contenido ahora supera el límite local de ${MESSAGE_MAX_LENGTH} caracteres (actual: ${html.length}).`,
          "warning"
        );
      }
    }, 0);
  }, [IMAGE_FEATURE_TEMP, MESSAGE_MAX_LENGTH]);

  // Custom header toolbar (PrimeReact headerTemplate) restricting visible controls
  const editorHeader = useMemo(
    () => (
      <div className="notification-rich-toolbar">
        {/* Headings */}
        <span className="ql-formats">
          <select className="ql-header" defaultValue="">
            <option value="1">H1</option>
            <option value="2">H2</option>
            <option value="3">H3</option>
            <option value="4">H4</option>
            <option value="5">H5</option>
            <option value="6">H6</option>
            <option value="">P</option>
          </select>
        </span>
        {/* Basic formatting */}
        <span className="ql-formats">
          <button className="ql-bold" aria-label="Negrilla"></button>
          <button className="ql-italic" aria-label="Itálica"></button>
          <button className="ql-underline" aria-label="Subrayado"></button>
        </span>
        {/* Lists */}
        <span className="ql-formats">
          <button
            className="ql-list"
            value="ordered"
            aria-label="Lista numerada"
          ></button>
          <button
            className="ql-list"
            value="bullet"
            aria-label="Lista con viñetas"
          ></button>
        </span>
        {/* Link + Image URL */}
        <span className="ql-formats">
          <button className="ql-link" aria-label="Link"></button>
          {IMAGE_FEATURE_TEMP && (
            <button
              type="button"
              className="ql-custom-image"
              onClick={handleInsertImage}
              aria-label="Insertar imagen por URL"
              title="Insertar imagen por URL"
              style={{ fontWeight: 600 }}
            >
              Img
            </button>
          )}
        </span>
      </div>
    ),
    [IMAGE_FEATURE_TEMP, handleInsertImage]
  );

  // Normalize links so they become absolute (add https:// if missing protocol)
  const normalizeLinks = (html) => {
    if (!html) return html;
    try {
      const container = document.createElement("div");
      container.innerHTML = html;
      const anchors = container.querySelectorAll("a[href]");
      anchors.forEach((a) => {
        const href = (a.getAttribute("href") || "").trim();
        // Skip if already absolute or special schemes
        if (/^(https?:|mailto:|tel:|#|\/)/i.test(href)) return;
        // Prepend https:// by default
        const normalized = "https://" + href.replace(/^\/+/, "");
        a.setAttribute("href", normalized);
      });
      return container.innerHTML;
    } catch (e) {
      return html;
    }
  };

  const handleChange = async () => {
    try {
      // Prepare HTML to send (prefer richMessage if present, else fallback to legacy textarea message)
      const prepareHtmlForSend = (html) => {
        if (!html) return "";
        // Sanitize first
        let sanitized = DOMPurify.sanitize(html);
        try {
          const container = document.createElement("div");
          container.innerHTML = sanitized;
          container.querySelectorAll("a[href]").forEach((a) => {
            // Ensure protocol
            const href = a.getAttribute("href") || "";
            if (!/^(https?:|mailto:|tel:|#|\/)/i.test(href)) {
              a.setAttribute("href", "https://" + href.replace(/^\/+/, ""));
            }
            // Ensure target/rel
            if (!a.getAttribute("target")) a.setAttribute("target", "_blank");
            const rel = (a.getAttribute("rel") || "")
              .split(/\s+/)
              .filter(Boolean);
            if (!rel.includes("noopener")) rel.push("noopener");
            if (!rel.includes("noreferrer")) rel.push("noreferrer");
            a.setAttribute("rel", rel.join(" "));
          });
          sanitized = container.innerHTML;
        } catch (_) {}
        return sanitized;
      };

      const richToSend =
        richMessage && richMessage.trim() !== ""
          ? prepareHtmlForSend(richMessage)
          : notification.message;
      const currentLength = richToSend.length;
      setMessageLength(currentLength);
      const payload = {
        notifications: [
          {
            // Using rich HTML if available; fallback retains backward compatibility
            message: richToSend,
            roles: sendToSpecificUsers
              ? ["ROLE_NOTIFICATION"]
              : notification.roles.map((rol) => rol.value),
            // Cambia el mapeo para mantener los IDs como strings
            customers: sendToSpecificUsers
              ? notification.customers
                  .split(",")
                  .map((id) => id.trim()) // Solo quita espacios
                  .filter((id) => id !== "") // Filtra valores vacíos
              : undefined,
            start_date: notification.start_date + "T00:00:00",
            end_date: notification.end_date + "T23:59:00",
          },
        ],
      };

      await Api.Notification.Create(payload);
      showNotification(
        "Enhorabuena",
        "La notificación se creó correctamente",
        "success"
      );
      setRichMessage("");
      setMessageLength(0);
      setNotification({
        message: "",
        roles: [],
        start_date: "",
        end_date: "",
        customers: "",
      });
      fetchLatestNotifications();
    } catch (error) {
      // Si el backend rechaza y la longitud excede el límite local, mensaje específico
      if (messageLength > MESSAGE_MAX_LENGTH) {
        showNotification(
          "Error",
          "Se han excedido los caracteres en el mensaje a enviar",
          "danger"
        );
      } else {
        showNotification("Error", error.message, "danger");
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await Api.Notification.Delete([deleteTarget.id]);
      showNotification("Éxito", "Notificación eliminada", "success");
      setShowDeleteModal(false);
      setDeleteTarget(null);
      if (expandedId === deleteTarget.id) {
        setExpandedId(null);
      }
      fetchLatestNotifications();
    } catch (error) {
      showNotification("Error", error.message, "danger");
    }
  };

  const DeleteModalContent = () => (
    <p>
      ¿Está seguro que desea eliminar la notificación #{deleteTarget?.id}?
    </p>
  );

  return (
    <div className="register-container-notifications">
      <div className="form-container-notifications">
        <h1>Centro de notificaciones</h1>
        <form className="form-register">
          <div className="">
            <div className="">
              {/* LEGACY TEXTAREA (comentado pero mantenido para historial / referencia). */}
              {/*
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display:'block', fontWeight:600 }}>Ingrese la notificación (modo texto plano - deprecado)</label>
                <textarea
                  className="notification-textarea"
                  placeholder="Agregue la nueva notificación"
                  name="message"
                  value={notification.message}
                  onChange={(e) =>
                    setNotification({ ...notification, message: e.target.value })
                  }
                />
              </div>
              */}
              {/* <p
                style={{
                  fontSize: "0.85rem",
                  color: "#888",
                  marginTop: "0.5rem",
                }}
              >
                El editor enriquecido ahora se envía al backend. El textarea
                anterior queda como referencia histórica.
              </p> */}
              {/* Rich Text Editor */}
              <div className="notification-rich-editor">
                <p style={{ marginTop: "1rem" }}>Editor enriquecido</p>
                <Editor
                  ref={editorRef}
                  value={richMessage}
                  onTextChange={(e) => {
                    const html = e.htmlValue || "";
                    const normalized = normalizeLinks(html);
                    setRichMessage(normalized);
                    // Actualizar longitud prevista a enviar (sin target/rel extras todavía)
                    const len = (normalized || "").length;
                    setMessageLength(len);
                  }}
                  headerTemplate={editorHeader}
                  style={{ height: "300px" }}
                />
                <div
                  style={{
                    marginTop: "0.4rem",
                    fontSize: "0.75rem",
                    fontFamily: "monospace",
                  }}
                >
                  <span>Caracteres: </span>
                  <span
                    style={{
                      color:
                        messageLength > MESSAGE_MAX_LENGTH
                          ? "red"
                          : messageLength > MESSAGE_MAX_LENGTH * 0.9
                          ? "#d87700"
                          : "#666",
                    }}
                  >
                    {messageLength}/{MESSAGE_MAX_LENGTH}{" "}
                    {messageLength > MESSAGE_MAX_LENGTH && "(Excedido)"}
                  </span>
                </div>
                {/* <small
                  style={{
                    display: "block",
                    marginTop: "0.5rem",
                    color: "#888",
                  }}
                >
                  Vista previa del contenido HTML que será enviado.
                </small> */}
                <div
                  className="notification-rich-preview"
                  style={{ marginTop: "1.5rem" }}
                >
                  <h3 style={{ marginBottom: "0.5rem" }}>Previsualización</h3>
                  {(() => {
                    const sanitized = DOMPurify.sanitize(richMessage || "");
                    const isEmpty =
                      !sanitized ||
                      sanitized === "<p><br></p>" ||
                      sanitized.replace(/<[^>]*>/g, "").trim() === "";
                    if (isEmpty) {
                      return (
                        <div className="notification-rich-preview-content empty">
                          (Sin contenido)
                        </div>
                      );
                    }
                    // Post-process only for preview: add target/rel safely
                    let finalHtml = sanitized;
                    try {
                      const fragContainer = document.createElement("div");
                      fragContainer.innerHTML = sanitized;
                      fragContainer.querySelectorAll("a[href]").forEach((a) => {
                        if (!a.getAttribute("target"))
                          a.setAttribute("target", "_blank");
                        const rel = (a.getAttribute("rel") || "")
                          .split(/\s+/)
                          .filter(Boolean);
                        if (!rel.includes("noopener")) rel.push("noopener");
                        if (!rel.includes("noreferrer")) rel.push("noreferrer");
                        a.setAttribute("rel", rel.join(" "));
                      });
                      finalHtml = fragContainer.innerHTML;
                    } catch (_) {}
                    return (
                      <div
                        className="notification-rich-preview-content"
                        dangerouslySetInnerHTML={{ __html: finalHtml }}
                      />
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
          <div className="pair-container">
            <div className="customer_id_type">
              Enviar a usuarios específicos
              <input
                type="checkbox"
                checked={sendToSpecificUsers}
                onChange={(e) => setSendToSpecificUsers(e.target.checked)}
              />
            </div>
          </div>
          {sendToSpecificUsers ? (
            <div className="pair-container">
              <div className="full-width">
                Ingrese los IDs de usuarios separados por comas
                <input
                  type="text"
                  className="user-ids-input"
                  placeholder="Ejemplo: 9,10,15"
                  value={notification.customers}
                  onChange={(e) =>
                    setNotification({
                      ...notification,
                      customers: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          ) : (
            <div className="pair-container">
              <div className="full-width">
                Seleccione el rol o roles
                <Select
                  isMulti
                  name="roles"
                  options={ROL_OPTIONS}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={notification.roles}
                  onChange={(selectedRoles) => {
                    setNotification({ ...notification, roles: selectedRoles });
                  }}
                  isDisabled={sendToSpecificUsers} // Deshabilitar cuando se envían a usuarios específicos
                />
              </div>
            </div>
          )}
          <div className="pair-container">
            <div className="customer-id_container">
              Fecha de inicio de la notificación
              <input
                className="start_date"
                type="date"
                id="start_date"
                name="start_date"
                value={notification.start_date}
                onChange={(e) =>
                  setNotification({
                    ...notification,
                    start_date: e.target.value,
                  })
                }
              />
            </div>
            <div className="customer_id_type">
              Fecha final de la notificación
              <input
                className="end_date"
                type="date"
                id="end_date"
                name="end_date"
                value={notification.end_date}
                onChange={(e) =>
                  setNotification({ ...notification, end_date: e.target.value })
                }
              />
            </div>
          </div>
          <div className="submit-buttons-container">
            <input
              className="button_register"
              type="button"
              onClick={handleChange}
              value="Enviar"
            />
          </div>
        </form>

        <section className="notifications-latest-section">
          <h2>Últimas notificaciones</h2>
          {loadingLatest ? (
            <p className="notifications-latest-empty">Cargando…</p>
          ) : latestNotifications.length === 0 ? (
            <p className="notifications-latest-empty">
              No hay notificaciones recientes.
            </p>
          ) : (
            <ul className="notifications-latest-list">
              {latestNotifications.map((item) => {
                const plainMessage = stripHtml(item.message);
                const isExpanded = expandedId === item.id;
                const preview = truncateText(plainMessage);
                const canExpand = plainMessage.length > 120;

                return (
                  <li key={item.id} className="notifications-latest-item">
                    <div className="notifications-latest-header">
                      <span className="notifications-latest-id">#{item.id}</span>
                      <button
                        type="button"
                        className="button_notification notifications-delete-btn"
                        onClick={() => {
                          setDeleteTarget(item);
                          setShowDeleteModal(true);
                        }}
                      >
                        Eliminar
                      </button>
                    </div>
                    {isExpanded ? (
                      <div
                        className="notifications-latest-message rich"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(item.message || ""),
                        }}
                      />
                    ) : (
                      <p className="notifications-latest-message">{preview}</p>
                    )}
                    {canExpand && (
                      <button
                        type="button"
                        className="notifications-expand-btn"
                        onClick={() =>
                          setExpandedId(isExpanded ? null : item.id)
                        }
                      >
                        {isExpanded ? "Ver menos" : "Ver más"}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
      {showDeleteModal && (
        <Modal
          isOpen={showDeleteModal}
          title="Eliminar notificación"
          Content={DeleteModalContent}
          onCancel={() => {
            setShowDeleteModal(false);
            setDeleteTarget(null);
          }}
          onSubmit={handleConfirmDelete}
        />
      )}
    </div>
  );
};

export default Notifications;
